<script lang="ts" setup>
  import { useViewQuery } from '@othercommerce/foundation';
  import { computed } from 'vue';
  import { selectContentEvent, useGoogleTagManager } from '@storefront/storefront';

  defineProps({
    categories: { type: Object, required: true },
  });

  const query = useViewQuery();
  const gtm = useGoogleTagManager();

  const filteredQuery = computed(() => {
    return Object.fromEntries(Object.entries(query.value || []).filter(([k]) => k != 'page'));
  });
</script>
<template>
  <div class="categories">
    <div v-if="categories.parent" class="categories__back">
      <RouterLink :href="$url(categories.parent.url, filteredQuery)">
        {{ $t('SHOP.COMMON.ACTION.BACK_TO') }}
        <span class="categories__parent-name">{{ categories.parent.name }}</span>
      </RouterLink>
    </div>
    <div v-if="categories.category" class="categories__current">
      <RouterLink :href="$url(categories.category.url, filteredQuery)">
        {{ categories.category.name }}
      </RouterLink>
    </div>
    <ul class="categories__list">
      <li v-for="category in categories.children" class="categories__item">
        <RouterLink :href="$url(category.url, filteredQuery)" @click="gtm.push(selectContentEvent('category', category.name))">
          <span class="categories__name">{{ category.name }}</span>
          <span class="categories__quantity">({{ category.products }})</span>
        </RouterLink>
      </li>
    </ul>
  </div>
</template>
<style lang="scss">
  :root {
    --sf-category-filters-color-hover: var(--color-primary);
    --sf-category-filters-back-font-size: 0.875rem;
    --sf-category-filters-font-size: 1rem;
    --sf-category-filters-active-font-weight: var(--font-weight-semi);
    --sf-category-filters-current-font-weight: var(--font-weight-bold);
    --sf-category-filters-current-font-size: 1rem;
    --sf-category-filters-current-color: var(--color-default);
  }

  .categories {
    display: flex;
    flex-direction: column;
    font-size: var(--sf-category-filters-font-size);

    &__back {
      font-size: var(--sf-category-filters-back-font-size);
      margin-bottom: 0.5rem;
    }

    &__parent-name {
      text-decoration: underline;
      margin-left: 0.375rem;
    }

    &__current {
      font-weight: var(--sf-category-filters-current-font-weight);
      font-size: var(--sf-category-filters-current-font-size);
      color: var(--sf-category-filters-current-color);
    }

    &__list {
      display: flex;
      flex-direction: column;
      list-style: none;
    }

    &__current + &__list {
      padding-left: 0.75rem;
    }

    a {
      display: inline-block;
      padding: 0.25rem 0;
    }

    @media (hover: hover) {
      &__current,
      &__list {
        a {
          text-decoration: underline;
          text-decoration-color: transparent;
          transition: text-decoration-color 0.2s ease-out;
        }

        a:hover {
          text-decoration-color: var(--primary-color);

          .categories__quantity {
            color: var(--primary-color);
          }
        }
      }
    }

    &__item {
      font-size: 0.875rem;

      a {
        &.active .categories__name {
          font-weight: var(--sf-category-filters-active-font-weight);
        }
      }
    }

    &__quantity {
      color: var(--sf-checkbox-label-available-color);
      padding-left: 0.25rem;
    }

    @media(max-width: 992px) {
      a {
        padding: 0.5rem 0;
      }
    }
  }
</style>
